// ── Certificates ("/certificates") — FR-CERT (NOW LIVE) ─────────────────────
// Reads the NOW-LIVE GET /certificates (view.certificates, ?orgId injected by the
// shared transport, scope-isolated bare { certificates:[…] }) via useHonestQuery
// and renders the rows incl. status + issued_at + an HONEST "document pending"
// when artifactRef is null (NO fabricated download link). SA/Admin get an Issue
// affordance (POST issue + a generated UUID Idempotency-Key). The 9 honest
// postures stay: loading is a skeleton, a denied/unavailable read renders its
// honest state, and an empty list shows the neutral empty state — never a blank.
//
// ── PARTICIPANT EXCLUSION (release gate) ─────────────────────────────────────
// The certificates ADMIN surface is STAFF-only (route roles = adminRoles);
// participant is denied at the route AND here (fail-closed). A participant's own
// certificate appears on their dashboard (own-scope read), not on this admin page.

import { CollectionToolbar, PageFrame, ProTable } from "@vjal/ui";
import { useMemo } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingReadinessServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingCertificatesResponseDto } from "../../services/coaching-readiness-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { CertificateIssueSection } from "./certificate-issue-section";
import { useCertificatesCollection } from "./certificates-collection";
import { canAccessCertificatesModule, canIssueCertificates, type CertificateRow } from "./certificates-helpers";

const services = getCoachingReadinessServices();

export function CertificatesPage({ role, routePath }: ProductScreenContext) {
  const moduleDenied = !canAccessCertificatesModule(role);
  const canIssue = canIssueCertificates(role);

  const query = useHonestQuery<CoachingCertificatesResponseDto>(
    coachingQueryKeys.readiness.certificates(),
    () => services.certificates.list(),
    {
      enabled: !moduleDenied,
      isEmpty: (data) => (data.certificates ?? []).length === 0
    }
  );

  const allRows = useMemo(() => (query.data?.certificates ?? []) as CertificateRow[], [query.data]);
  const collection = useCertificatesCollection(allRows, routePath);

  if (moduleDenied) {
    return (
      <section aria-label="Certificates" className="col-span-12" data-testid="certificates-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to certificates"
            message="The certificates area is for coaching staff. Any certificate you earn appears on your dashboard."
          />
        </div>
      </section>
    );
  }

  const posture =
    query.posture === null && collection.total === 0 && allRows.length > 0 ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  return (
    <PageFrame title="Certificates" subtitle="Issued certificates and document status." className="col-span-12">
      <section aria-label="Certificates" className="flex flex-col gap-4" data-testid="certificates-page">
        {canIssue ? <CertificateIssueSection /> : null}

        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by certificate, participant, or cohort…"
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white" data-testid="certificates-shell">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              loadingVariant="rows"
              title={posture === "empty" ? "No certificates yet" : undefined}
              message={
                posture === "empty"
                  ? "No certificates have been issued yet. A passing capstone evaluation or a staff issue will list them here."
                  : undefined
              }
            />
          ) : (
            /* F3: real @vjal/ui ProTable-1 recipe export under Page-1 shell. */
            <ProTable
              collection={collection}
              className="p-0"
              tableProps={{
                caption: "Issued certificates and their document status",
                emptyTitle: "No certificates match your search",
                emptyMessage: "Clear or change the filters above to widen the view.",
              }}
              stableChipNames
            />
          )}
        </div>
      </section>
    </PageFrame>
  );
}